import { defineStore } from 'pinia'
import TaskService from '@/services/TaskService'

export const TASK_STATUSES = [
  { key: 'pending', icon: 'mdi-checkbox-blank-circle-outline', color: 'warning' },
  { key: 'in progress', icon: 'mdi-play-circle-outline', color: 'info' },
  { key: 'completed', icon: 'mdi-check-circle-outline', color: 'success' },
  { key: 'archived', icon: 'mdi-archive-outline', color: 'secondary' },
]

const normalizeStatus = status => {
  const value = String(status || 'pending').toLowerCase().trim()
  if (['in progress', 'in-progress', 'active', 'started'].includes(value)) return 'in progress'
  if (['completed', 'complete', 'done', 'finished'].includes(value)) return 'completed'
  if (value === 'archived') return 'archived'
  return 'pending'
}

const normalizeOptionalNumber = value => value === null || value === undefined || value === '' ? null : Number(value)

const extractItems = response => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data?.items)) return response.data.items
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.items)) return response.items
  return []
}

const extractItem = response => response?.data?.item || response?.data || response?.item || response

const normalizeTask = rawTask => {
  if (!rawTask) return null

  return {
    id: rawTask.id || rawTask._id,
    projectId: rawTask.project_id || rawTask.projectId || rawTask.bucketId || null,
    projectName: rawTask.project_name || rawTask.projectName || '',
    title: rawTask.title || '',
    description: rawTask.description || '',
    status: normalizeStatus(rawTask.status),
    duration: Number(rawTask.duration_hours ?? rawTask.duration ?? 1),
    scheduledDay: normalizeOptionalNumber(rawTask.scheduled_day ?? rawTask.scheduledDay),
    scheduledHour: normalizeOptionalNumber(rawTask.scheduled_hour ?? rawTask.scheduledHour),
    scheduledStart: rawTask.scheduled_start || rawTask.scheduledStart || null,
    scheduledEnd: rawTask.scheduled_end || rawTask.scheduledEnd || null,
    timezone: rawTask.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    icon: rawTask.icon || 'mdi-checkbox-marked-circle-outline',
    completedAt: rawTask.completed_at || rawTask.completedAt || null,
    createdAt: rawTask.created_at || rawTask.createdAt || new Date().toISOString(),
    updatedAt: rawTask.updated_at || rawTask.updatedAt || null,
    syncError: false,
  }
}

const dateForDayHour = (day, hour) => {
  if (day === null || day === undefined || hour === null || hour === undefined) return null

  const today = new Date()
  const todayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1
  const date = new Date(today)

  date.setDate(today.getDate() + (Number(day) - todayIndex))
  date.setHours(Number(hour), 0, 0, 0)

  return date
}

const toApiPayload = task => {
  const scheduledStart = dateForDayHour(task.scheduledDay, task.scheduledHour)
  const scheduledEnd = scheduledStart
    ? new Date(scheduledStart.getTime() + (Number(task.duration || 1) * 60 * 60 * 1000))
    : null

  return {
    project_id: task.projectId,
    title: task.title,
    description: task.description || undefined,
    status: normalizeStatus(task.status),
    duration_hours: Number(task.duration || 1),
    scheduled_day: task.scheduledDay ?? null,
    scheduled_hour: task.scheduledHour ?? null,
    scheduled_start: task.scheduledStart || scheduledStart?.toISOString() || null,
    scheduled_end: task.scheduledEnd || scheduledEnd?.toISOString() || null,
    timezone: task.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    icon: task.icon || undefined,
    completed_at: normalizeStatus(task.status) === 'completed' ? (task.completedAt || new Date().toISOString()) : null,
  }
}

export const useTaskStore = defineStore('TaskStore', {
  state: () => ({
    tasks: [],
    filters: {
      projectId: null,
      includeArchived: true,
    },
    loading: false,
    saving: false,
    error: null,
    syncingTaskIds: [],
    lastSyncedAt: null,
  }),

  getters: {
    statuses: () => TASK_STATUSES,

    visibleTasks: state => state.tasks.filter(task => state.filters.includeArchived || task.status !== 'archived'),

    tasksByStatus: state => status => state.tasks.filter(task => (
      task.status === status &&
      (state.filters.includeArchived || task.status !== 'archived')
    )),

    countByStatus: state => status => state.tasks.filter(task => task.status === status).length,

    totalTasks: state => state.tasks.length,

    activeTasks: state => state.tasks.filter(task => task.status !== 'archived'),

    getTaskById: state => taskId => state.tasks.find(task => task.id === taskId),
  },

  actions: {
    setTaskSyncing(taskId, syncing) {
      if (!taskId) return
      if (syncing && !this.syncingTaskIds.includes(taskId)) this.syncingTaskIds.push(taskId)
      if (!syncing) this.syncingTaskIds = this.syncingTaskIds.filter(id => id !== taskId)
    },

    setProjectFilter(projectId) {
      this.filters.projectId = projectId || null
      return this.fetchTasks()
    },

    replaceTask(localId, replacement) {
      const normalized = normalizeTask(replacement)
      if (!normalized?.id) return

      const index = this.tasks.findIndex(task => task.id === localId)
      if (index !== -1) {
        this.tasks.splice(index, 1, { ...this.tasks[index], ...normalized })
      } else {
        this.tasks.push(normalized)
      }
    },

    async fetchTasks(params = {}) {
      this.loading = true
      this.error = null

      try {
        const query = {
          include_completed: true,
          include_archived: this.filters.includeArchived,
          ...params,
        }

        if (this.filters.projectId) query.project_id = this.filters.projectId

        const response = await TaskService.getTasks(query)
        const items = extractItems(response).map(normalizeTask).filter(Boolean)

        this.tasks = items
        this.lastSyncedAt = new Date().toISOString()

        return items
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Unable to load tasks'
        console.error('[TaskStore] fetchTasks:', error)
        return []
      } finally {
        this.loading = false
      }
    },

    async createTask(payload) {
      if (!payload.projectId || !payload.title?.trim()) return null

      const task = normalizeTask({
        id: `task-local-${Date.now()}`,
        project_id: payload.projectId,
        project_name: payload.projectName,
        title: payload.title.trim(),
        description: payload.description || '',
        status: payload.status || 'pending',
        duration_hours: Math.max(1, Math.min(Number(payload.duration) || 1, 4)),
        scheduled_day: normalizeOptionalNumber(payload.scheduledDay),
        scheduled_hour: normalizeOptionalNumber(payload.scheduledHour),
        icon: payload.icon || 'mdi-checkbox-marked-circle-outline',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })

      this.tasks.push(task)
      this.saving = true
      this.setTaskSyncing(task.id, true)

      try {
        const response = await TaskService.createTask(toApiPayload(task))
        this.replaceTask(task.id, extractItem(response))
        this.lastSyncedAt = new Date().toISOString()
      } catch (error) {
        task.syncError = true
        this.error = error.response?.data?.message || error.message || 'Unable to create task'
        console.error('[TaskStore] createTask:', error)
      } finally {
        this.setTaskSyncing(task.id, false)
        this.saving = false
      }

      return task
    },

    async updateTask(taskId, updates = {}) {
      const task = this.getTaskById(taskId)
      if (!task) return false

      const snapshot = { ...task }
      Object.assign(task, updates, { status: normalizeStatus(updates.status || task.status) })

      if (task.status === 'completed' && !task.completedAt) task.completedAt = new Date().toISOString()
      if (task.status !== 'completed') task.completedAt = null

      this.setTaskSyncing(task.id, true)

      try {
        const response = await TaskService.updateTask(task.id, toApiPayload(task))
        this.replaceTask(task.id, extractItem(response))
        this.lastSyncedAt = new Date().toISOString()
        return true
      } catch (error) {
        Object.assign(task, snapshot, { syncError: true })
        this.error = error.response?.data?.message || error.message || 'Unable to update task'
        console.error('[TaskStore] updateTask:', error)
        return false
      } finally {
        this.setTaskSyncing(task.id, false)
      }
    },

    async moveTaskStatus(taskId, status) {
      return this.updateTask(taskId, { status: normalizeStatus(status) })
    },
  },
})
